import React, { useState, useMemo, useEffect } from 'react';
import {
  ArrowLeft, Target, TrendingUp, TrendingDown, Edit2, Trash2,
  ChevronDown, ChevronUp, Star, Plus, BookOpen, Layers,
  Activity, DollarSign, BarChart2, AlertCircle, CheckCircle, X,
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  LineChart, Line, XAxis, YAxis,
} from 'recharts';
import { useAppState } from '../store/AppContext';
import { updateFundMetadata, getSessionUserId, loadFundTargets } from '../utils/storage';
import { DEFAULT_ASSET_CLASSES } from '../utils/initialData';
import { Card } from './ui/Card';
import { SAAWorkspace, SAAComparison, TargetRow } from './SAAPlanner';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Asset, Bucket } from '../types';
import {
  getAssetCurrentValue, getAssetAnnualIncome,
  resolveClassColor, getAssetUnrealizedGain, getAssetCapitalGainIncome,
  getAssetCostBasisFromLedger,
} from '../utils/calculations';
import { formatVND } from '../utils/format';
import { Money, SensitiveValue } from './ui/SensitiveValue';
import { HoldingsTable } from './HoldingsTable';
import { fetchAllPrices } from '../utils/priceUpdateService';
import { v4 } from '../utils/uuid';

// ── Types ─────────────────────────────────────────────────────────────────────
interface FundNote {
  id: string;
  date: string;
  rating: number; // 1-5
  text: string;
}

// ── localStorage helpers ───────────────────────────────────────────────────────
function loadNotes(fundId: string): FundNote[] {
  try { return JSON.parse(localStorage.getItem(`wealthos:fund-notes:${fundId}`) || '[]'); }
  catch { return []; }
}
function persistNotes(fundId: string, notes: FundNote[]) {
  localStorage.setItem(`wealthos:fund-notes:${fundId}`, JSON.stringify(notes));
}

// ── Tooltip styles ─────────────────────────────────────────────────────────────
const TT = {
  contentStyle: { backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)', borderRadius: 10, fontSize: 11, color: 'var(--tx-1)' },
  itemStyle: { color: 'var(--tx-3)' },
  cursor: { stroke: 'var(--tx-5)', strokeDasharray: '3 3' },
};

// ── Section heading ────────────────────────────────────────────────────────────
function Sec({ icon, label, color = '#10b981', right }: { icon: React.ReactNode; label: string; color?: string; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${color}18`, border: `1px solid ${color}28` }}>
          {icon}
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
      </div>
      {right}
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KPI({ label, value, sub, color = 'var(--tx-1)', accent }: { label: string; value: React.ReactNode; sub?: React.ReactNode; color?: string; accent?: string }) {
  return (
    <div style={{ borderRadius: 14, padding: '18px 20px', background: 'var(--bg-card)', border: '1px solid var(--bd)', position: 'relative', overflow: 'hidden', flex: 1, minWidth: 0 }}>
      {accent && <div style={{ position: 'absolute', top: -24, right: -24, width: 80, height: 80, borderRadius: '50%', background: accent, pointerEvents: 'none' }} />}
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--tx-4)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color, letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--tx-4)', lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

// ── Star rating ───────────────────────────────────────────────────────────────
function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => onChange(i)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
          <Star size={18} fill={(hover || value) >= i ? '#fbbf24' : 'none'} color={(hover || value) >= i ? '#fbbf24' : 'var(--tx-5)'} />
        </button>
      ))}
    </div>
  );
}

// ── Asset mini chart ──────────────────────────────────────────────────────────
function PriceSparkline({ assetId, color }: { assetId: string; color: string }) {
  const { state } = useAppState();
  const history = useMemo(() =>
    state.priceHistory
      .filter(p => p.assetId === assetId)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-30)
      .map(p => ({ date: p.date.slice(0, 10), price: p.price })),
    [state.priceHistory, assetId]
  );
  if (history.length < 2) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--tx-5)', fontSize: 11 }}>
      <Activity size={12} /> Chưa đủ dữ liệu giá
    </div>
  );
  return (
    <ResponsiveContainer width="100%" height={80}>
      <LineChart data={history} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey="date" tick={{ fontSize: 9, fill: 'var(--tx-5)' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 9, fill: 'var(--tx-5)' }} tickLine={false} axisLine={false} width={50}
          tickFormatter={v => formatVND(v)} domain={['auto', 'auto']} />
        <Tooltip {...TT} formatter={(v: any) => [formatVND(v), 'Giá']} />
        <Line type="monotone" dataKey="price" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function FundDetailPage({ fundId, onBack }: { fundId: string; onBack: () => void }) {
  const { state, dispatch } = useAppState();
  const { assets, buckets, assetClasses, liabilities, priceHistory, moneyTransactions = [], holdings = [] } = state;

  // Fund & its assets — tous les hooks AVANT le early return
  const bucket     = buckets.find(b => b.id === fundId);
  const fundAssets = useMemo(() => assets.filter(a => a.bucketId === fundId), [assets, fundId]);

  const classBreakdown = useMemo(() => {
    const map: Record<string, { value: number; color: string }> = {};
    fundAssets.forEach(a => {
      const c  = a.assetClass || 'Khác';
      const cl = resolveClassColor(c, assetClasses);
      if (!map[c]) map[c] = { value: 0, color: cl };
      map[c].value += getAssetCurrentValue(a);
    });
    return Object.entries(map).map(([name, { value, color }]) => ({ name, value, color }))
      .sort((a, b) => b.value - a.value);
  }, [fundAssets, assetClasses]);

  // Notes
  const [notes, setNotes] = useState<FundNote[]>(() => loadNotes(fundId));
  const [noteText, setNoteText] = useState('');
  const [noteRating, setNoteRating] = useState(5);
  const [expandedAsset,  setExpandedAsset]  = useState<string | null>(null);
  const [refreshingPrices, setRefreshingPrices] = useState(false);

  const handleRefreshPrices = async () => {
    setRefreshingPrices(true);
    try {
      const results = await fetchAllPrices(assets, () => {}, holdings);
      if (results.length > 0) {
        dispatch({ type: 'BULK_UPDATE_PRICES', payload: results.map(r => ({ code: r.code, price: r.price })) });
        // Cập nhật marketPrice cho holdings trong memory
        const updatedHoldings = holdings.map(h => {
          const match = results.find(r => r.code.toUpperCase() === h.symbol.toUpperCase());
          return match ? { ...h, marketPrice: match.price, provider: match.source, lastUpdated: new Date().toISOString() } : h;
        });
        dispatch({ type: 'SET_HOLDINGS', payload: updatedHoldings });
        // Rollup lại từng composite asset
        const compositeIds = Array.from(new Set(
          holdings
            .filter(h => results.some(r => r.code.toUpperCase() === h.symbol.toUpperCase()))
            .map(h => h.assetId)
        ));
        compositeIds.forEach(aid => dispatch({ type: 'ROLLUP_COMPOSITE_ASSET', payload: { assetId: aid } }));
      }
    } finally {
      setRefreshingPrices(false);
    }
  };
  const [noteEditId, setNoteEditId] = useState<string | null>(null);
  // ── Edit metadata modal ────────────────────────────────────────────────────
  const [editOpen,   setEditOpen]   = useState(false);
  const [editName,   setEditName]   = useState('');
  const [editDesc,   setEditDesc]   = useState('');
  const [editTarget, setEditTarget] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [toastMsg,   setToastMsg]   = useState('');

  // ── SAA (Strategic Asset Allocation) state ────────────────────────────────
  const [activeTab,  setActiveTab]  = useState<'overview' | 'planning'>('overview');
  const [saaTargets, setSaaTargets] = useState<TargetRow[]>([]);
  const [saaLoaded,  setSaaLoaded]  = useState(false);

  // Khóa cuộn body khi modal mở
  useEffect(() => {
    document.body.style.overflow = editOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [editOpen]);

  // Tải SAA targets từ DB khi mount
  useEffect(() => {
    loadFundTargets(fundId).then(rows => {
      setSaaTargets(rows.map(r => {
        // Tìm theo ID (UUID), fallback tên, fallback DEFAULT_ASSET_CLASSES (cho dữ liệu cũ dùng slug)
        const cls = assetClasses.find(c => c.id === r.asset_class_id)
                 ?? assetClasses.find(c => c.name === r.asset_class_id)
                 ?? DEFAULT_ASSET_CLASSES.find(c => c.id === r.asset_class_id);
        return {
          assetClassId:    cls?.id    ?? r.asset_class_id,
          assetClassName:  cls?.name  ?? r.asset_class_id,
          assetClassColor: cls?.color ?? '#94a3b8',
          weight:   r.target_weight,
          value:    r.target_value,
          expYield: r.expected_yield,
          expCap:   r.expected_cap_gain,
        };
      }));
      setSaaLoaded(true);
    });
  }, [fundId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditModal = () => {
    if (!bucket) return;
    setEditName(bucket.name);
    setEditDesc(bucket.description || '');
    setEditTarget(bucket.targetValue > 0 ? String(bucket.targetValue) : '');
    setEditOpen(true);
  };

  const handleSaveMeta = async () => {
    if (!bucket || !editName.trim()) return;
    setSaving(true);
    try {
      const userId = await getSessionUserId();
      if (!userId) {
        window.dispatchEvent(new CustomEvent('wealthos:sync-error', {
          detail: {
            source: 'Chỉnh sửa quỹ',
            message: 'Phiên làm việc hết hạn — không thể lưu.',
            hint: 'Vui lòng đăng xuất và đăng nhập lại.',
            isAuthError: true,
          },
        }));
        return;
      }
      // Chỉ lấy số, bỏ dấu chấm/phẩy định dạng
      const targetNum = editTarget ? Number(String(editTarget).replace(/[^0-9]/g, '')) : 0;

      const ok = await updateFundMetadata(fundId, {
        name: editName.trim(),
        description: editDesc.trim(),
        targetValue: targetNum,
      }, userId);

      if (ok) {
        // Cập nhật state ngay lập tức — tiến độ % thay đổi tức thì
        dispatch({ type: 'UPDATE_BUCKET', payload: {
          ...bucket,
          name:        editName.trim(),
          description: editDesc.trim(),
          targetValue: targetNum,
        }});
        setEditOpen(false);
        setToastMsg('Đã cập nhật chiến lược quỹ thành công');
        setTimeout(() => setToastMsg(''), 3000);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveNotesList = (updated: FundNote[]) => {
    setNotes(updated);
    persistNotes(fundId, updated);
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    if (noteEditId) {
      const updated = notes.map(n => n.id === noteEditId ? { ...n, text: noteText, rating: noteRating } : n);
      saveNotesList(updated);
      setNoteEditId(null);
    } else {
      const n: FundNote = { id: v4(), date: new Date().toISOString(), rating: noteRating, text: noteText.trim() };
      saveNotesList([n, ...notes]);
    }
    setNoteText('');
    setNoteRating(5);
  };

  const handleEditNote = (n: FundNote) => {
    setNoteEditId(n.id);
    setNoteText(n.text);
    setNoteRating(n.rating);
  };

  const handleDeleteNote = (id: string) => {
    saveNotesList(notes.filter(n => n.id !== id));
  };

  // ── Calculations (before early return so hooks remain unconditional) ──────────
  const totalValue   = fundAssets.reduce((s, a) => s + getAssetCurrentValue(a), 0);
  const totalCost    = fundAssets.reduce((s, a) => s + getAssetCostBasisFromLedger(a.id, moneyTransactions as any[]), 0);
  const totalGain    = totalValue - totalCost;
  const growthPct    = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;

  // Linked debt: CHỈ lấy khoản nợ được gán trực tiếp vào quỹ này qua fundId
  // Không dùng linkedAssetId vì field đó chỉ để tracking tài sản thế chấp, không đại diện cho nợ của quỹ
  const fundAssetIds = new Set(fundAssets.map(a => a.id));
  const linkedDebt   = liabilities
    .filter(l => l.fundId === fundId)
    .reduce((s, l) => s + l.outstandingBalance, 0);

  const fundNAV      = totalValue - linkedDebt;

  // Income
  const estimatedIncome = fundAssets.reduce((s, a) => s + getAssetAnnualIncome(a), 0);
  const yieldBased      = fundAssets.reduce((s, a) => s + (a.currentPrice ?? 0) * (a.quantity ?? 1) * ((a.yieldRate ?? 0) / 100), 0);
  const incomeYieldPct  = totalValue > 0 ? (estimatedIncome / totalValue) * 100 : 0;
  const incomeGap       = estimatedIncome - yieldBased;

  // ── Tỷ suất HOẠCH ĐỊNH — từ SAA Planning, dùng cho 3 KPI cards + SAAComparison "Kỳ vọng" ──
  const saaWeightSum    = saaTargets.reduce((s, t) => s + t.weight, 0);
  const fundYieldRate   = saaLoaded && saaTargets.length > 0 && saaWeightSum > 0
    ? saaTargets.reduce((s, t) => s + t.weight * t.expYield, 0) / saaWeightSum
    : 0;
  const fundCapGainRate = saaLoaded && saaTargets.length > 0 && saaWeightSum > 0
    ? saaTargets.reduce((s, t) => s + t.weight * t.expCap, 0) / saaWeightSum
    : 0;
  const fundTotalReturn = fundYieldRate + fundCapGainRate;
  const planNavTarget      = bucket?.targetValue ?? 0;
  const totalYieldIncome   = planNavTarget * fundYieldRate   / 100;
  const totalCapGainIncome = planNavTarget * fundCapGainRate / 100;

  // ── Tỷ suất THỰC TẾ — dùng cho cột "Thực tế" trong SAAComparison ─────────────
  // Yield thực tế: tổng giao dịch INCOME liên quan đến tài sản trong quỹ này trong năm hiện tại
  const currentYear       = new Date().getFullYear();
  const yieldActualIncome = (moneyTransactions as any[])
    .filter((t: any) => t.type === 'INCOME'
      && new Date(t.date).getFullYear() === currentYear
      && (
        (t.asset_id && fundAssetIds.has(t.asset_id))
        || (!t.asset_id && t.fund_id === fundId)
      ))
    .reduce((s: number, t: any) => s + (t.net_amount ?? 0), 0);
  const actualYieldRate   = totalValue > 0 ? (yieldActualIncome / totalValue) * 100 : 0;
  // Cap Gain thực tế: (NAV hiện tại - Vốn đầu tư) / Vốn đầu tư — growthPct đã tính sẵn
  const actualCapGainRate = growthPct;


  if (!bucket) return (
    <div style={{ textAlign: 'center', padding: 60, color: 'var(--tx-4)' }}>
      Không tìm thấy quỹ · <button onClick={onBack} style={{ color: '#10b981', background: 'none', border: 'none', cursor: 'pointer' }}>← Quay lại</button>
    </div>
  );

  // Target progress
  const targetPct = bucket.targetValue > 0 ? Math.min((totalValue / bucket.targetValue) * 100, 150) : null;
  const targetDiff = bucket.targetValue > 0 ? totalValue - bucket.targetValue : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Back + header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: 500, cursor: 'pointer', background: 'var(--bg-card-el)', border: '1px solid var(--bd)', color: 'var(--tx-3)', transition: 'background 0.1s' }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-row-hover)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-el)'; }}>
          <ArrowLeft size={13} /> Chiến lược Quỹ
        </button>
        {/* Vùng metadata — click để chỉnh sửa */}
        <button
          onClick={openEditModal}
          title="Chỉnh sửa thông tin quỹ"
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 14,
            background: 'none', border: 'none', cursor: 'pointer',
            textAlign: 'left', padding: '6px 8px', borderRadius: 12,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-row-hover)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 13, background: `linear-gradient(135deg, ${bucket.color}30 0%, ${bucket.color}12 100%)`, border: `1.5px solid ${bucket.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Target size={18} color={bucket.color} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--tx-1)', letterSpacing: '-0.025em', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {bucket.name}
              </h1>
              <Edit2 size={13} style={{ color: 'var(--tx-5)', flexShrink: 0, opacity: 0.7 }} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--tx-4)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {bucket.description || 'Nhấn để thêm mô tả'} · {fundAssets.length} tài sản
            </div>
          </div>
        </button>
        {/* Target pill — cũng click được */}
        <button
          onClick={openEditModal}
          title="Chỉnh sửa mục tiêu"
          style={{ padding: '7px 14px', borderRadius: 20, background: `${bucket.color}18`, border: `1px solid ${bucket.color}30`, fontSize: 12, fontWeight: 600, color: bucket.color, cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0, whiteSpace: 'nowrap' }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = `${bucket.color}28`; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = `${bucket.color}18`; }}
        >
          {bucket.targetValue > 0 ? `Mục tiêu: ${formatVND(bucket.targetValue)}` : '+ Đặt mục tiêu'}
        </button>
      </div>

      {/* ── Tab switcher ── */}
      <div style={{ display: 'flex', gap: 4, padding: '4px', background: 'var(--bg-card-el)', border: '1px solid var(--bd)', borderRadius: 12, alignSelf: 'flex-start' }}>
        {(['overview', 'planning'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 20px', borderRadius: 9, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', border: 'none', transition: 'all 0.15s',
              background: activeTab === tab ? 'var(--bg-card)' : 'none',
              color: activeTab === tab ? 'var(--tx-1)' : 'var(--tx-4)',
              boxShadow: activeTab === tab ? 'var(--shadow)' : 'none',
            }}
          >
            {tab === 'overview' ? 'Tổng quan' : 'Hoạch định'}
          </button>
        ))}
      </div>

      {/* ══ Overview tab ══ */}
      {activeTab === 'overview' && (<>

      {/* ── 3 tỷ suất Hoạch định (từ SAA Planning, tính trên NAV Target) ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <KPI
          label="Yield Rate Kỳ Vọng (Hoạch định)"
          value={saaTargets.length > 0 ? `${fundYieldRate.toFixed(2)}%` : '—'}
          sub={saaTargets.length > 0
            ? <><Money value={totalYieldIncome} />/năm · dựa trên NAV mục tiêu</>
            : <>Chưa có hoạch định SAA</>}
          color="#fbbf24"
          accent="radial-gradient(circle, #fbbf2420 0%, transparent 70%)"
        />
        <KPI
          label="Tăng trưởng Vốn dự phóng (Cap Gain)"
          value={saaTargets.length > 0 ? `${fundCapGainRate.toFixed(2)}%` : '—'}
          sub={saaTargets.length > 0
            ? <><Money value={totalCapGainIncome} />/năm · dựa trên NAV mục tiêu</>
            : <>Chưa có hoạch định SAA</>}
          color="#38bdf8"
          accent="radial-gradient(circle, #38bdf820 0%, transparent 70%)"
        />
        <KPI
          label="Tổng tỷ suất sinh lời Mục tiêu"
          value={saaTargets.length > 0 ? `${fundTotalReturn.toFixed(2)}%` : '—'}
          sub={saaTargets.length > 0
            ? <>Yield {fundYieldRate.toFixed(1)}% + CapGain {fundCapGainRate.toFixed(1)}% · NAV đích: <Money value={planNavTarget} /></>
            : <>Thiết lập SAA trong tab Hoạch định</>}
          color={fundTotalReturn >= 8 ? '#10b981' : fundTotalReturn >= 4 ? '#fbbf24' : 'var(--tx-4)'}
          accent={`radial-gradient(circle, ${bucket.color}20 0%, transparent 70%)`}
        />
      </div>

      {/* SAA Comparison — Tiến độ Lớp Tài sản theo Hoạch định */}
      {saaLoaded && saaTargets.length > 0 && (
        <SAAComparison
          targets={saaTargets}
          assetClasses={assetClasses}
          fundAssets={fundAssets}
          totalValue={totalValue}
          fundYieldRate={actualYieldRate}
          fundCapGainRate={actualCapGainRate}
          bucket={bucket}
        />
      )}

      {/* ── Target & Income ── */}
      <div className="rsp-grid-2" style={{ gap: 14 }}>

        {/* Target progress */}
        <Card>
          <div style={{ padding: '18px 20px' }}>
            <Sec icon={<Target size={13} color={bucket.color} />} label="Tiến độ Mục tiêu" color={bucket.color} />
            {bucket.targetValue > 0 ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--tx-1)', letterSpacing: '-0.025em' }}>{targetPct!.toFixed(1)}%</div>
                    <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>hoàn thành</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, color: 'var(--tx-3)', fontWeight: 600 }}><Money value={totalValue} /></div>
                    <div style={{ fontSize: 11, color: 'var(--tx-5)' }}>/ <Money value={bucket.targetValue} /></div>
                  </div>
                </div>

                {/* Progress track */}
                <div style={{ position: 'relative', height: 10, borderRadius: 5, backgroundColor: 'var(--bd)', overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{
                    height: '100%', borderRadius: 5, transition: 'width 0.7s ease',
                    width: `${Math.min(targetPct!, 100)}%`,
                    background: targetPct! >= 100
                      ? 'linear-gradient(90deg, #10b981, #059669)'
                      : targetPct! >= 75
                        ? `linear-gradient(90deg, ${bucket.color}, ${bucket.color}cc)`
                        : `linear-gradient(90deg, #fbbf24, ${bucket.color})`,
                  }} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {targetDiff! >= 0
                    ? <><CheckCircle size={13} color="#10b981" /><span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>Dư <Money value={targetDiff!} /></span></>
                    : <><AlertCircle size={13} color="#fbbf24" /><span style={{ fontSize: 12, color: '#fbbf24', fontWeight: 600 }}>Còn thiếu <Money value={Math.abs(targetDiff!)} /></span></>
                  }
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--tx-5)', fontSize: 12 }}>
                Chưa đặt mục tiêu cho quỹ này
              </div>
            )}
          </div>
        </Card>

        {/* Income comparison */}
        <Card>
          <div style={{ padding: '18px 20px' }}>
            <Sec icon={<DollarSign size={13} color="#fbbf24" />} label="Đối soát Thu nhập" color="#fbbf24" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <IncomeBar label="Kế hoạch (SAA)" value={totalYieldIncome} max={Math.max(totalYieldIncome, yieldActualIncome, 1)} color="#38bdf8" />
              <IncomeBar label="Thực tế (giao dịch)" value={yieldActualIncome} max={Math.max(totalYieldIncome, yieldActualIncome, 1)} color="#10b981" />

              <div style={{ paddingTop: 10, borderTop: '1px solid var(--bd)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>Khoảng cách (Gap)</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: (yieldActualIncome - totalYieldIncome) >= 0 ? '#10b981' : '#f87171' }}>
                  <Money value={yieldActualIncome - totalYieldIncome} prefix={(yieldActualIncome - totalYieldIncome) >= 0 ? '+' : ''} />
                </span>
              </div>
              {yieldActualIncome === 0 && totalYieldIncome === 0 && (
                <div style={{ fontSize: 11, color: 'var(--tx-5)', fontStyle: 'italic' }}>
                  Chưa có tài sản nào thiết lập yield rate
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* ── Asset class breakdown + drill-down table ── */}
      <div className={classBreakdown.length > 0 ? 'fund-chart-layout' : ''} style={{ gap: 14 }}>

        {/* Pie chart */}
        {classBreakdown.length > 0 && (
          <Card>
            <div style={{ padding: '18px 20px' }}>
              <Sec icon={<Layers size={13} color="#a78bfa" />} label="Cơ cấu Tài sản" color="#a78bfa" />
              <div style={{ height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={classBreakdown} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2}>
                      {classBreakdown.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)', borderRadius: 10, fontSize: 11 }}
                      formatter={(v: any) => [formatVND(v), '']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                {classBreakdown.map(c => (
                  <div key={c.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: c.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>{c.name}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-2)' }}><Money value={c.value} /></span>
                      <span style={{ fontSize: 10, color: 'var(--tx-5)', marginLeft: 5 }}>
                        {totalValue > 0 ? ((c.value / totalValue) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Asset drill-down table */}
        <Card>
          <div style={{ padding: '18px 20px 12px' }}>
            <Sec icon={<BarChart2 size={13} color="#38bdf8" />} label="Danh sách Tài sản" color="#38bdf8" />
          </div>
          {fundAssets.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--tx-5)', fontSize: 12 }}>
              Chưa có tài sản trong quỹ này
            </div>
          ) : (
            <div className="asset-drilldown-wrap">
              {/* Table header */}
              <div className="asset-drilldown-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 110px 36px', gap: 0, padding: '6px 20px', borderBottom: '1px solid var(--bd)' }}>
                {['Tài sản', 'Giá trị', 'Tỷ trọng', 'Lãi/Lỗ', 'Yield / CapGain', ''].map(h => (
                  <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx-5)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: h === '' ? 'center' : h === 'Tài sản' ? 'left' : 'right' }}>{h}</div>
                ))}
              </div>

              {fundAssets
                .sort((a, b) => getAssetCurrentValue(b) - getAssetCurrentValue(a))
                .map((a, i, arr) => {
                  const val     = getAssetCurrentValue(a);
                  const gain    = getAssetUnrealizedGain(a);
                  const ledgerCost = getAssetCostBasisFromLedger(a.id, moneyTransactions as any[]);
                  const gainPct = ledgerCost > 0 ? (gain / ledgerCost) * 100 : 0;
                  const income  = getAssetAnnualIncome(a);
                  const weight  = totalValue > 0 ? (val / totalValue) * 100 : 0;
                  const isExp   = expandedAsset === a.id;
                  const isLast  = i === arr.length - 1;

                  return (
                    <div key={a.id}>
                      {/* Row */}
                      <div
                        className="asset-drilldown-row"
                        onClick={() => setExpandedAsset(isExp ? null : a.id)}
                        style={{
                          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 110px 36px', gap: 0,
                          padding: '12px 20px', cursor: 'pointer',
                          borderBottom: isExp || !isLast ? '1px solid var(--bd-sub)' : 'none',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-row-hover)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--tx-1)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                          <Badge label={a.assetClass} size="xs" color={resolveClassColor(a.assetClass, assetClasses)} />
                        </div>
                        <div style={{ textAlign: 'right', alignSelf: 'center' }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-1)' }}><Money value={val} /></div>
                        </div>
                        <div style={{ textAlign: 'right', alignSelf: 'center' }}>
                          <div style={{ fontSize: 12, color: 'var(--tx-2)' }}>{weight.toFixed(1)}%</div>
                          <div style={{ width: '100%', height: 3, borderRadius: 2, backgroundColor: 'var(--bd)', marginTop: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${weight}%`, height: '100%', borderRadius: 2, backgroundColor: bucket.color }} />
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', alignSelf: 'center' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: gain >= 0 ? '#10b981' : '#f87171' }}>
                            {gain >= 0 ? '+' : ''}{gainPct.toFixed(1)}%
                          </div>
                          <div style={{ fontSize: 10, color: gain >= 0 ? '#059669' : '#dc2626' }}>
                            <Money value={gain} prefix={gain >= 0 ? '+' : ''} />
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', alignSelf: 'center' }}>
                          {(a.yieldRate ?? 0) > 0 || (a.capitalGainRate ?? 0) > 0 ? (
                            <>
                              <div style={{ fontSize: 11, color: '#fbbf24' }}>Y: {(a.yieldRate ?? 0).toFixed(1)}%</div>
                              <div style={{ fontSize: 11, color: '#38bdf8' }}>G: {(a.capitalGainRate ?? 0).toFixed(1)}%</div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', borderTop: '1px solid var(--bd-sub)', marginTop: 2, paddingTop: 2 }}>
                                Σ {((a.yieldRate ?? 0) + (a.capitalGainRate ?? 0)).toFixed(1)}%
                              </div>
                            </>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--tx-5)' }}>—</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'center', color: 'var(--tx-4)' }}>
                          {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </div>
                      </div>

                      {/* Drill-down expanded row */}
                      {isExp && (
                        <div style={{ padding: '16px 20px 20px', borderBottom: isLast ? 'none' : '1px solid var(--bd)', background: 'var(--bg-card-el)' }}>
                            <div className="rsp-grid-3" style={{ gap: 16 }}>
                            {/* 3 tỷ suất per-asset */}
                            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, marginBottom: 4 }}>
                              {[
                                { label: 'Yield Rate', value: (a.yieldRate ?? 0).toFixed(2) + '%', color: '#fbbf24', sub: formatVND(getAssetAnnualIncome(a)) + '/năm' },
                                { label: 'Capital Gain', value: (a.capitalGainRate ?? 0).toFixed(2) + '%', color: '#38bdf8', sub: formatVND(getAssetCapitalGainIncome(a)) + '/năm' },
                                { label: 'Tổng tỷ suất', value: ((a.yieldRate ?? 0) + (a.capitalGainRate ?? 0)).toFixed(2) + '%', color: '#10b981', sub: formatVND(getAssetAnnualIncome(a) + getAssetCapitalGainIncome(a)) + '/năm' },
                              ].map(kpi => (
                                <div key={kpi.label} style={{ flex: 1, padding: '10px 14px', borderRadius: 10, background: 'var(--bg-card)', border: `1px solid ${kpi.color}25` }}>
                                  <div style={{ fontSize: 10, color: 'var(--tx-5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{kpi.label}</div>
                                  <div style={{ fontSize: 20, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 3 }}>{kpi.sub}</div>
                                </div>
                              ))}
                            </div>
                            {/* Price history sparkline */}
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                Biến động giá
                              </div>
                              <PriceSparkline assetId={a.id} color={bucket.color} />
                            </div>

                            {/* Income breakdown */}
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                Dòng tiền tài sản
                              </div>
                              {(a.incomeEvents && a.incomeEvents.length > 0) ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 80, overflowY: 'auto' }}>
                                  {a.incomeEvents.sort((x, y) => x.month - y.month).map(ev => (
                                    <div key={ev.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                      <span style={{ color: 'var(--tx-4)' }}>Tháng {ev.month} · {ev.label}</span>
                                      <span style={{ fontWeight: 600, color: '#fbbf24' }}><Money value={ev.amount} /></span>
                                    </div>
                                  ))}
                                </div>
                              ) : income > 0 ? (
                                <div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: 'var(--tx-4)' }}>Thu nhập ước tính/năm</span>
                                    <span style={{ fontWeight: 600, color: '#fbbf24' }}><Money value={income} /></span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 4 }}>
                                    <span style={{ color: 'var(--tx-5)' }}>Trung bình/tháng</span>
                                    <span style={{ color: 'var(--tx-3)' }}><Money value={income / 12} /></span>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ fontSize: 11, color: 'var(--tx-5)', fontStyle: 'italic' }}>Chưa thiết lập dòng tiền</div>
                              )}
                            </div>

                            {/* Notes */}
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Ghi chú tài sản</div>
                              <div style={{ fontSize: 11, color: 'var(--tx-3)', lineHeight: 1.6 }}>{a.notes || <span style={{ color: 'var(--tx-5)', fontStyle: 'italic' }}>Không có ghi chú</span>}</div>
                            </div>
                          </div>

                          {/* Extra: linked debt */}
                          {liabilities.filter(l => l.linkedAssetId === a.id).map(l => (
                            <div key={l.id} style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: 'rgba(248,113,113,0.07)', border: '1px solid rgba(248,113,113,0.18)', display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                              <span style={{ color: 'var(--tx-4)' }}>🔗 Nợ liên kết: {l.name}</span>
                              <span style={{ fontWeight: 600, color: '#f87171' }}>Dư nợ: <Money value={l.outstandingBalance} /></span>
                            </div>
                          ))}

                          {/* Holdings table — chỉ hiển thị cho composite assets */}
                          {a.isComposite && (
                            <HoldingsTable
                              assetId={a.id}
                              onRefreshPrices={handleRefreshPrices}
                              refreshing={refreshingPrices}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </Card>
      </div>

      {/* ── Strategic Notes ── */}
      <Card>
        <div style={{ padding: '18px 20px' }}>
          <Sec icon={<BookOpen size={13} color="#a78bfa" />} label="Nhật ký Quản trị" color="#a78bfa" />

          {/* Add / edit note form */}
          <div style={{ marginBottom: 20, padding: '14px 16px', borderRadius: 12, background: 'var(--bg-card-el)', border: '1px solid var(--bd)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)' }}>
                {noteEditId ? 'Chỉnh sửa ghi chú' : 'Thêm ghi chú mới'}
              </span>
              <StarRating value={noteRating} onChange={setNoteRating} />
            </div>
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Nhận định, chiến lược điều chỉnh, rủi ro cần theo dõi..."
              rows={3}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 9, fontSize: 12, resize: 'vertical', outline: 'none', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)', fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              {noteEditId && (
                <button onClick={() => { setNoteEditId(null); setNoteText(''); setNoteRating(5); }}
                  style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', background: 'var(--bg-card)', border: '1px solid var(--bd)', color: 'var(--tx-3)' }}>
                  Hủy
                </button>
              )}
              <button onClick={handleAddNote} disabled={!noteText.trim()}
                style={{ padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: noteText.trim() ? 'pointer' : 'not-allowed', background: noteText.trim() ? '#a78bfa' : 'var(--bg-card-el)', color: noteText.trim() ? '#fff' : 'var(--tx-5)', border: 'none', transition: 'background 0.15s', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Plus size={12} />{noteEditId ? 'Lưu thay đổi' : 'Lưu ghi chú'}
              </button>
            </div>
          </div>

          {/* Notes list */}
          {notes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--tx-5)', fontSize: 12, fontStyle: 'italic' }}>
              Chưa có ghi chú nào · Hãy ghi lại nhận định đầu tiên
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {notes.map(n => (
                <div key={n.id} style={{ padding: '14px 16px', borderRadius: 11, background: 'var(--bg-card-el)', border: '1px solid var(--bd)', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} size={12} fill={i <= n.rating ? '#fbbf24' : 'none'} color={i <= n.rating ? '#fbbf24' : 'var(--tx-6)'} />
                      ))}
                      <span style={{ fontSize: 10, color: 'var(--tx-5)', marginLeft: 4 }}>
                        {new Date(n.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button onClick={() => handleEditNote(n)} style={{ width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--bg-card)', border: 'none', color: 'var(--tx-4)' }}>
                        <Edit2 size={11} />
                      </button>
                      <button onClick={() => handleDeleteNote(n.id)} style={{ width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--bg-card)', border: 'none', color: '#f87171' }}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--tx-2)', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{n.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      </>)}

      {/* ══ Planning tab (SAA Workspace) ══ */}
      {activeTab === 'planning' && (
        <SAAWorkspace
          fundId={fundId}
          bucket={bucket}
          assetClasses={assetClasses}
          targets={saaTargets}
          setTargets={setSaaTargets}
          refNAV={bucket.targetValue > 0 ? bucket.targetValue : totalValue}
          fundNAV={fundNAV}
          onSaved={msg => {
            setToastMsg(msg);
            setTimeout(() => setToastMsg(''), 3000);
          }}
          onAddAssetClass={cls => dispatch({ type: 'ADD_ASSET_CLASS', payload: cls })}
        />
      )}

      {/* ── Toast thành công ── */}
      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: 'calc(28px + env(safe-area-inset-bottom, 0px))', left: '50%',
          transform: 'translateX(-50%)', zIndex: 200,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '13px 20px', borderRadius: 14, maxWidth: 'calc(100vw - 40px)',
          background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
          border: '1px solid rgba(16,185,129,0.4)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          animation: 'toastIn 0.25s ease',
          whiteSpace: 'nowrap',
        }}>
          <CheckCircle size={16} color="#10b981" />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#d1fae5' }}>{toastMsg}</span>
        </div>
      )}

      {/* ── Edit Metadata Modal (Bottom Sheet on mobile, centered on desktop) ── */}
      {editOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 150,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            backgroundColor: 'var(--bg-overlay)',
            backdropFilter: 'blur(6px)',
          }}
          onClick={() => !saving && setEditOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 560,
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--bd)',
              borderRadius: '20px 20px 0 0',
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              overflow: 'hidden',
            }}
          >
            {/* Header modal */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid var(--bd)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: `${bucket.color}20`, border: `1px solid ${bucket.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Edit2 size={14} color={bucket.color} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-1)' }}>Chỉnh sửa Quỹ</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 1 }}>Thay đổi sẽ được lưu ngay lên cloud</div>
                </div>
              </div>
              <button
                onClick={() => !saving && setEditOpen(false)}
                style={{ display: 'flex', padding: 8, borderRadius: 8, background: 'var(--bg-card-el)', border: '1px solid var(--bd)', cursor: 'pointer', color: 'var(--tx-4)' }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Form */}
            <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Tên quỹ */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>
                  Tên quỹ
                </label>
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  placeholder="Vd: Quỹ FIRE — Tự do Tài chính"
                  style={{
                    width: '100%', padding: '13px 14px', borderRadius: 11, fontSize: 15, fontWeight: 600,
                    backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
                    color: 'var(--tx-1)', outline: 'none', boxSizing: 'border-box',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = bucket.color; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--bd)'; }}
                />
              </div>

              {/* Mục tiêu */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>
                  Mục tiêu tài chính (VND)
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editTarget ? Number(editTarget).toLocaleString('vi-VN') : ''}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^0-9]/g, '');
                      setEditTarget(raw);
                    }}
                    placeholder="Vd: 40.000.000.000 (40 tỷ)"
                    style={{
                      width: '100%', padding: '13px 14px 13px 44px', borderRadius: 11, fontSize: 15, fontWeight: 600,
                      backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
                      color: 'var(--tx-1)', outline: 'none', boxSizing: 'border-box',
                      transition: 'border-color 0.15s', fontVariantNumeric: 'tabular-nums',
                    }}
                    onFocus={e => { e.target.style.borderColor = bucket.color; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--bd)'; }}
                  />
                  <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--tx-5)', pointerEvents: 'none' }}>₫</span>
                </div>
                {editTarget && Number(editTarget) > 0 && (
                  <div style={{ fontSize: 11, color: bucket.color, marginTop: 5, fontWeight: 600 }}>
                    = {formatVND(Number(editTarget))}
                  </div>
                )}
              </div>

              {/* Ghi chú / Chiến lược */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>
                  Ghi chú · Chiến lược
                </label>
                <textarea
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  placeholder="Vd: Tài sản dài hạn, cổ phiếu cổ tức cao, BĐS cho thuê..."
                  rows={3}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 11, fontSize: 14,
                    backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
                    color: 'var(--tx-1)', outline: 'none', resize: 'none', boxSizing: 'border-box',
                    lineHeight: 1.5, transition: 'border-color 0.15s', fontFamily: 'inherit',
                  }}
                  onFocus={e => { e.target.style.borderColor = bucket.color; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--bd)'; }}
                />
              </div>

              {/* Nút bấm — to, rõ ràng cho Mobile */}
              <div style={{ display: 'flex', gap: 10, paddingTop: 4, paddingBottom: 4 }}>
                <button
                  onClick={() => !saving && setEditOpen(false)}
                  disabled={saving}
                  style={{
                    flex: 1, padding: '14px', borderRadius: 12, fontSize: 14, fontWeight: 600,
                    background: 'var(--bg-card-el)', border: '1px solid var(--bd)',
                    color: 'var(--tx-3)', cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.5 : 1, transition: 'background 0.12s',
                  }}
                >
                  Huỷ
                </button>
                <button
                  onClick={handleSaveMeta}
                  disabled={saving || !editName.trim()}
                  style={{
                    flex: 2, padding: '14px', borderRadius: 12, fontSize: 15, fontWeight: 700,
                    background: (!editName.trim() || saving)
                      ? 'var(--bd)'
                      : `linear-gradient(135deg, ${bucket.color} 0%, ${bucket.color}cc 100%)`,
                    border: 'none',
                    color: (!editName.trim() || saving) ? 'var(--tx-5)' : '#fff',
                    cursor: saving || !editName.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'opacity 0.15s',
                    opacity: saving ? 0.7 : 1,
                    boxShadow: (!editName.trim() || saving) ? 'none' : `0 4px 16px ${bucket.color}40`,
                  }}
                >
                  {saving ? (
                    <>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite' }} />
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Lưu chiến lược
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// ── Income bar helper ─────────────────────────────────────────────────────────
function IncomeBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: color }} />
          <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>{label}</span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color }}><Money value={value} /></span>
      </div>
      <div style={{ height: 5, borderRadius: 3, backgroundColor: 'var(--bd)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, backgroundColor: color, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}
